"use client";

import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "components/products/muggleTestV0/components/ui/button";
import { Checkbox } from "components/products/muggleTestV0/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/products/muggleTestV0/components/ui/dialog";
import { Input } from "components/products/muggleTestV0/components/ui/input";
import { Label } from "components/products/muggleTestV0/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/products/muggleTestV0/components/ui/select";
import { Textarea } from "components/products/muggleTestV0/components/ui/textarea";
import {
  ENTERPRISE_SALES_CONCURRENCY_LEVELS,
  ENTERPRISE_SALES_INTERESTS,
  ENTERPRISE_SALES_SEAT_RANGES,
  ENTERPRISE_SALES_TEST_CASE_SCALES,
  ENTERPRISE_SALES_TIMELINES,
  createDefaultEnterpriseSalesInquiryForm,
} from "constants/enterpriseSalesInquiryConfig";
import { MUGGLE_CUSTOMER_FACING_EMAILS } from "constants/muggleCustomerFacingEmail";
import { COMMON_COPY } from "copy/common";
import { PUBLIC_PAGES_COPY } from "copy/publicPages";
import { useCopy } from "i18n/useCopy";
import {
  buildEnterpriseSalesInquiryMailto,
  formatEnterpriseSalesInquiryEmail,
} from "lib/enterprise/formatEnterpriseSalesInquiryEmail";
import { EnterpriseSalesInquiryService } from "services/muggleTest/enterpriseSalesInquiryService";
import type { IEnterpriseSalesInquiryFormState } from "types/enterprise/enterprise-sales-inquiry-form.types";
import {
  EnterpriseSalesConcurrencyEnum,
  EnterpriseSalesSeatRangeEnum,
  EnterpriseSalesTestCaseScaleEnum,
  EnterpriseSalesTimelineEnum,
} from "types/enterprise/enterprise-sales-inquiry.enums";
import type { EnterpriseSalesInquiryModalProps } from "types/enterprise/EnterpriseSalesInquiryModalProps";

export const ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_KEY = "modal";
export const ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_VALUE = "enterprise-sales-inquiry";
const WORK_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Modal to collect enterprise customization preferences and open a pre-filled sales email.
 *
 * @param searchParamKey - URL query param key that controls open state
 * @param searchParamValue - URL query param value that controls open state
 * @param submissionSourceLabel - Shown inside the generated email body
 */
export const EnterpriseSalesInquiryModal: React.FC<EnterpriseSalesInquiryModalProps> = ({
  searchParamKey = ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_KEY,
  searchParamValue = ENTERPRISE_SALES_INQUIRY_MODAL_SEARCH_PARAM_VALUE,
  submissionSourceLabel,
}) => {
  const copy = useCopy();

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState<IEnterpriseSalesInquiryFormState>(createDefaultEnterpriseSalesInquiryForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isOpen = searchParams.get(searchParamKey) === searchParamValue;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setForm(createDefaultEnterpriseSalesInquiryForm());
        setSubmitError(null);
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextOpen) {
            next.set(searchParamKey, searchParamValue);
          } else {
            next.delete(searchParamKey);
          }
          return next;
        },
        { replace: true }
      );
    },
    [searchParamKey, searchParamValue, setSearchParams]
  );

  const mailtoHref = useMemo(() => {
    const { subject, body } = formatEnterpriseSalesInquiryEmail({
      form,
      submissionSourceLabel,
    });
    return buildEnterpriseSalesInquiryMailto({
      recipientEmail: MUGGLE_CUSTOMER_FACING_EMAILS.ENTERPRISE_SALES,
      subject,
      body,
    });
  }, [form, submissionSourceLabel]);

  const handleSubmitInquiryForm = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setSubmitError(null);
      setIsSubmitting(true);
      try {
        const contactName = form.contactName.trim();
        const workEmail = form.workEmail.trim();
        const companyName = form.companyName.trim();

        if (!contactName) {
          throw new Error(copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.validation.contactNameRequired));
        }
        if (!workEmail || !WORK_EMAIL_REGEX.test(workEmail)) {
          throw new Error(copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.validation.workEmailInvalid));
        }
        if (!companyName) {
          throw new Error(copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.validation.companyNameRequired));
        }

        const interests = ENTERPRISE_SALES_INTERESTS.filter((interest) => form.interests[interest]);
        const requestPayload = {
          submissionSourceLabel: submissionSourceLabel,
          contactName: contactName,
          workEmail: workEmail,
          companyName: companyName,
          estimatedSeats: form.estimatedSeats,
          concurrentWorkflows: form.concurrentWorkflows,
          testCaseScale: form.testCaseScale,
          timeline: form.timeline,
          interests: interests,
          comments: form.comments.trim(),
        };

        if (isAuthenticated) {
          const authToken = await getAccessTokenSilently();
          await EnterpriseSalesInquiryService.createInquiryAsync({
            authToken: authToken,
            request: requestPayload,
          });
        } else {
          await EnterpriseSalesInquiryService.createPublicInquiryAsync({
            request: requestPayload,
          });
        }

        window.location.href = mailtoHref;
      } catch (error) {
        console.error("Failed to persist enterprise sales inquiry", error);
        if (error instanceof Error && error.message.length > 0) {
          setSubmitError(error.message);
        } else {
          setSubmitError(copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.validation.saveFailed));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, getAccessTokenSilently, isAuthenticated, mailtoHref, submissionSourceLabel, copy]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="fullscreen"
        size="md"
        className="sm:max-h-[min(90vh,720px)]"
        aria-describedby="enterprise-sales-dialog-desc"
      >
        <DialogHeader className="px-6 sm:px-8 pt-4 pb-4 border-b">
          <DialogTitle>{copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.title)}</DialogTitle>
          <DialogDescription id="enterprise-sales-dialog-desc">
            {copy.format({
              text: PUBLIC_PAGES_COPY.enterpriseSalesInquiry.description,
              params: { salesEmail: MUGGLE_CUSTOMER_FACING_EMAILS.ENTERPRISE_SALES },
            })}
          </DialogDescription>
        </DialogHeader>

        <form className="flex flex-1 min-h-0 flex-col" onSubmit={handleSubmitInquiryForm}>
          <div className="flex-1 overflow-y-auto min-h-0 px-6 sm:px-8 pt-1 pb-4 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-contact-name">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.contactName.label)}
              </Label>
              <Input
                id="enterprise-sales-contact-name"
                autoComplete="name"
                placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.contactName.placeholder)}
                value={form.contactName}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, contactName: e.target.value }));
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-work-email">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.workEmail.label)}
              </Label>
              <Input
                id="enterprise-sales-work-email"
                type="email"
                autoComplete="email"
                placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.workEmail.placeholder)}
                value={form.workEmail}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, workEmail: e.target.value }));
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-company">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.companyName.label)}
              </Label>
              <Input
                id="enterprise-sales-company"
                autoComplete="organization"
                placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.companyName.placeholder)}
                value={form.companyName}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, companyName: e.target.value }));
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-seats">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.estimatedSeats.label)}
              </Label>
              <Select
                value={form.estimatedSeats}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, estimatedSeats: value as EnterpriseSalesSeatRangeEnum }));
                }}
              >
                <SelectTrigger id="enterprise-sales-seats">
                  <SelectValue
                    placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.estimatedSeats.placeholder)}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ENTERPRISE_SALES_SEAT_RANGES.map((seatRange) => (
                    <SelectItem key={seatRange} value={seatRange}>
                      {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.seatRange[seatRange])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-concurrency">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.concurrentWorkflows.label)}
              </Label>
              <Select
                value={form.concurrentWorkflows}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, concurrentWorkflows: value as EnterpriseSalesConcurrencyEnum }));
                }}
              >
                <SelectTrigger id="enterprise-sales-concurrency">
                  <SelectValue
                    placeholder={copy.text(
                      PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.concurrentWorkflows.placeholder
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ENTERPRISE_SALES_CONCURRENCY_LEVELS.map((concurrencyLevel) => (
                    <SelectItem key={concurrencyLevel} value={concurrencyLevel}>
                      {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.concurrency[concurrencyLevel])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-test-case-scale">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.testCaseScale.label)}
              </Label>
              <Select
                value={form.testCaseScale}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, testCaseScale: value as EnterpriseSalesTestCaseScaleEnum }));
                }}
              >
                <SelectTrigger id="enterprise-sales-test-case-scale">
                  <SelectValue
                    placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.testCaseScale.placeholder)}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ENTERPRISE_SALES_TEST_CASE_SCALES.map((testCaseScale) => (
                    <SelectItem key={testCaseScale} value={testCaseScale}>
                      {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.testCaseScale[testCaseScale])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-timeline">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.timeline.label)}
              </Label>
              <Select
                value={form.timeline}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, timeline: value as EnterpriseSalesTimelineEnum }));
                }}
              >
                <SelectTrigger id="enterprise-sales-timeline">
                  <SelectValue
                    placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.timeline.placeholder)}
                  />
                </SelectTrigger>
                <SelectContent>
                  {ENTERPRISE_SALES_TIMELINES.map((timeline) => (
                    <SelectItem key={timeline} value={timeline}>
                      {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.timeline[timeline])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <fieldset className="space-y-3 rounded-md border border-border p-3 sm:p-4">
              <legend className="px-1 text-sm font-medium">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.interests.legend)}
              </legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ENTERPRISE_SALES_INTERESTS.map((interest) => (
                  <label
                    key={interest}
                    className="flex cursor-pointer items-start gap-3 rounded-md border border-transparent p-2 hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={form.interests[interest]}
                      onCheckedChange={(checked) => {
                        setForm((prev) => ({
                          ...prev,
                          interests: {
                            ...prev.interests,
                            [interest]: checked === true,
                          },
                        }));
                      }}
                      aria-labelledby={`enterprise-interest-${interest}`}
                    />
                    <span id={`enterprise-interest-${interest}`} className="text-sm leading-snug text-muted-foreground">
                      {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.interest[interest])}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="space-y-2">
              <Label htmlFor="enterprise-sales-comments">
                {copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.comments.label)}
              </Label>
              <Textarea
                id="enterprise-sales-comments"
                placeholder={copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.fields.comments.placeholder)}
                className="min-h-[100px] resize-y"
                value={form.comments}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, comments: e.target.value }));
                }}
              />
            </div>
            {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
          </div>

          <DialogFooter className="gap-2 sm:gap-0 px-6 sm:px-8 py-4 border-t bg-background">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              {copy.text(COMMON_COPY.actions.cancel)}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.submitting)
                : copy.text(PUBLIC_PAGES_COPY.enterpriseSalesInquiry.submit)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
